import axios, { AxiosError } from 'axios';
import { Dispatch } from 'react';
import { showFeedbackModal } from '../UserFeedbackActions';
import { translate } from '../../../i18n/translate';
import { readErrorMessage } from '../../../utils/errorMessage';

// Licensing error codes - these are handled by the LicenseOverlay / LicenseErrorHandler,
// so handleApiError should never show a duplicate feedback toast for them.
const LICENSING_ERROR_CODES = new Set([
  'LICENSE_EXPIRED',
  'SECTION_NOT_LICENSED',
  'AI_FEATURE_NOT_LICENSED',
  'LICENSE_READ_ONLY',
  'USAGE_LIMIT_REACHED',
  'ADDON_NOT_ASSIGNED',
]);

export const handleApiError = (error: any, dispatch: Dispatch<any>) => {
  // Silently ignore axios.Cancel rejections (e.g. from the license request guard)
  if (axios.isCancel(error)) return;

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;

    // Silently ignore licensing 403s - overlay or LicenseErrorHandler handles these
    const errorCode = (axiosError.response?.data as any)?.code;
    if (errorCode && LICENSING_ERROR_CODES.has(errorCode)) return;

    if (axiosError.response && axiosError.response.data) {
      const errorMessage = readErrorMessage(axiosError, 'An unknown error occurred');

      // When we fall back, the server sent no prose we can safely show - log the
      // raw response for engineers (console only, never to the user) so the
      // failure is diagnosable without leaking payload content into the UI.
      if (errorMessage === 'An unknown error occurred') {
        console.error('[handleApiError] Unrenderable error payload:', {
          status: axiosError.response.status,
          url: axiosError.config?.url,
          data: axiosError.response.data,
        });
      }

      dispatch(
        showFeedbackModal({
          showModal: true,
          message: errorMessage,
          modalType: 'error',
          duration: 3000,
        })
      );
    } else {
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: translate('Network error, please try again'),
          modalType: 'error',
          duration: 3000,
        })
      );
    }
  } else {
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: translate('An unexpected error occurred'),
        modalType: 'error',
        duration: 3000,
      })
    );
  }
};
